import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { CheckCircle, ArrowRight, Sparkles } from 'lucide-react'

gsap.registerPlugin(ScrollTrigger)

const pricingPlans = [
  {
    name: 'Free',
    description: 'Suitable for early stage small teams, startups, or organizations.',
    features: [
      'Up to 15 team members',
      'Employee Profile Management',
      'Self Review & Manager Review',
      '1 Active Job Opening',
      'Up to 100 Candidates Database',
    ],
  },
  {
    name: 'Starter',
    description: 'Perfect for organizations (BEM/UKM) and small business requiring structured HRIS.',
    features: [
      'All benefits from Free',
      'Up to 100 team members',
      'Assessment Sessions & Psychometric Tests',
      '9-Box Grid Visualization',
      'Unlimited Candidate Database',
      'Report Export (PDF/CSV)',
    ],
  },
  {
    name: 'Professional',
    description: 'AI-based end-to-end solution for companies that requires strategic insights.',
    features: [
      'All benefits from Starter',
      'Starts from 250 team members',
      'AI-Generated IDP Drafts (coming soon)',
      'AI Talent Reports & 9-Box Grid',
      'Psychometric Test Builder',
      'Unlimited Active Job Opening',
      'Dedicated Onboarding',
    ],
  },
  {
    name: 'Enterprise',
    description: 'Strategic partnership for large corporations that requires an integrated solution.',
    features: [
      'All benefits from Professional',
      'Unlimited team members',
      'Consultant & Workshop Service',
      'Custom Competency Framework',
      'API & SSO Integration',
      'Dedicated Success Manager',
    ],
  },
]

export function LandingPricing() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.set(headerRef.current, { opacity: 0, y: 24 })
      ScrollTrigger.create({
        trigger: headerRef.current,
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(headerRef.current, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }),
      })

      gsap.set(cardRefs.current.filter(Boolean), { opacity: 0, y: 30 })
      ScrollTrigger.create({
        trigger: cardRefs.current[0],
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(cardRefs.current.filter(Boolean), {
          opacity: 1, y: 0, duration: 0.55, ease: 'power2.out', stagger: 0.1,
        }),
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} id="pricing" className="relative bg-[#359d9e] py-16 overflow-hidden">
      {/* Decorative background patterns */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute left-0 top-0 w-[374px] h-[722px]">
          <svg viewBox="0 0 374 722" fill="none" className="w-full h-full">
            <path d="M0 0C200 100 300 300 200 500C100 700 0 722 0 722V0Z" fill="white" fillOpacity="0.1" />
          </svg>
        </div>
        <div className="absolute right-0 top-0 w-[415px] h-[730px]">
          <svg viewBox="0 0 415 730" fill="none" className="w-full h-full">
            <path d="M415 0C215 100 115 300 215 500C315 700 415 730 415 730V0Z" fill="white" fillOpacity="0.1" />
          </svg>
        </div>
      </div>

      <div className="relative z-10 flex flex-col items-center gap-12 px-6 md:px-10 lg:px-[120px] max-w-screen-xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className="flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-[32px] font-semibold leading-[40px] tracking-[-0.5px] text-white sm:text-[40px] sm:leading-[48px] lg:text-[48px] lg:leading-[58px] lg:tracking-[-1px]">
            Simple pricing for all kinds of usage
          </h2>
          {/* TBA notice */}
          <div className="inline-flex items-center gap-2 bg-white/15 border border-white/30 rounded-full px-5 py-2 mt-2">
            <span className="w-2 h-2 rounded-full bg-yellow-300 flex-shrink-0 animate-pulse" />
            <span className="text-sm text-white font-medium">Pricing coming soon — early access available now</span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 gap-6 w-full max-w-[1200px] sm:grid-cols-2 xl:grid-cols-4">
          {pricingPlans.map((plan, i) => (
            <div key={plan.name} ref={(el) => { cardRefs.current[i] = el }} className="bg-white rounded-xl p-6 flex flex-col">
              {/* Plan Header */}
              <div className="mb-5">
                <h3 className="text-[22px] font-semibold text-[#000f0f] mb-2 lg:text-[24px]">{plan.name}</h3>
                <p className="text-sm text-[#1a2727] min-h-[54px]">{plan.description}</p>
              </div>

              {/* TBA price block */}
              <div className="mb-5 py-3 px-4 bg-[#f2fbfa] border border-[#d5f2ef] rounded-lg flex items-center gap-3">
                <span className="text-[13px] font-bold text-[#359d9e] tracking-wider uppercase">TBA</span>
                <span className="text-xs text-[#1a2727]">Pricing coming soon</span>
              </div>

              {/* CTA Button */}
              <a
                href="/#book-demo"
                className="w-full mb-6 inline-flex items-center justify-center gap-2 rounded px-4 py-2 text-sm font-medium transition-colors bg-[#359d9e] text-white hover:bg-[#27777a]"
              >
                Book A Demo
                <ArrowRight className="w-4 h-4" />
              </a>

              {/* Divider */}
              <div className="border-t border-[#f0f0f0] mb-4" />

              {/* Features */}
              <div className="flex flex-col gap-3">
                {plan.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#359d9e] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#1a2727]">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <p className="text-white/70 text-sm text-center max-w-md">
          Interested in early access? <a href="/login" className="text-white underline underline-offset-2 hover:text-white/90">Book a demo</a> and we'll reach out with pricing details.
        </p>
      </div>
    </section>
  )
}
